import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  getSportsFavoritesServerSnapshot,
  getSportsFavoritesSnapshot,
  subscribeSportsFavorites,
  toggleSportsFavorite,
  type SportsFavoriteMode,
  type SportsFavoritesSnapshot,
} from '@/domain/sports-favorites';

export type { SportsFavoritesSnapshot };

/** Client-side prematch / live event favorites (sessionStorage / localStorage). */
export function useSportsFavorites() {
  const snapshot = useSyncExternalStore(
    subscribeSportsFavorites,
    getSportsFavoritesSnapshot,
    getSportsFavoritesServerSnapshot,
  );

  const liveFavoriteIds = useMemo(
    () => new Set(snapshot.liveIds),
    [snapshot.liveIds],
  );

  const prematchFavoriteIds = useMemo(
    () => new Set(snapshot.prematchIds),
    [snapshot.prematchIds],
  );

  const isFavorite = useCallback(
    (eventId: number, mode: SportsFavoriteMode) =>
      mode === 'live' ? liveFavoriteIds.has(eventId) : prematchFavoriteIds.has(eventId),
    [liveFavoriteIds, prematchFavoriteIds],
  );

  const toggleFavorite = useCallback((eventId: number, mode: SportsFavoriteMode) => {
    toggleSportsFavorite(eventId, mode);
  }, []);

  return {
    prematchIds: snapshot.prematchIds,
    liveIds: snapshot.liveIds,
    liveFavoriteIds,
    hasPrematchFavorites: snapshot.hasPrematchFavorites,
    isFavorite,
    toggleFavorite,
  };
}
